import { createApi } from "@reduxjs/toolkit/query/react";
import { publicBaseQuery } from "../base/public/publicBaseQuery";
import type {
  AssignmentReportInfoResponse,
  GetAssignmentReportInfoRequest,
} from "@/types/endpoints/reports/GetAssignmentReportInfo";
import type {
  AssignmentReportDataResponse,
  GetAssignmentReportDataRequest,
} from "@/types/endpoints/reports/GetAssignmentReportData";
import type {
  PublicAiReportsResponse,
  GetPublicAiReportsRequest,
} from "@/types/endpoints/reports/GetPublicAiReports";
import type {
  GetAssignmentReportResultsRequest,
  GetAssignmentReportResultsResponse,
} from "@/types/endpoints/reports/GetAssignmentReportResults";

export const publicReportApi = createApi({
  reducerPath: "publicReportApi",
  baseQuery: publicBaseQuery(),
  tagTypes: ["ReportInfo", "ReportData"],
  keepUnusedDataFor: 300,
  endpoints: (builder) => ({
    getAssignmentReportInfo: builder.query<
      AssignmentReportInfoResponse,
      GetAssignmentReportInfoRequest
    >({
      query: ({ token }) => ({
        url: `/api/assignment-report/${token}/info.json`,
        method: "GET",
      }),
      providesTags: (result, error, { token }) => [
        { type: "ReportInfo", id: token },
      ],
    }),

    getAssignmentReportData: builder.query<
      AssignmentReportDataResponse,
      GetAssignmentReportDataRequest
    >({
      query: ({ token, attemptId }) => {
        const fields = [
          "attempts",
          "actions",
          "violations",
          "identities",
          "webinar_sessions",
          "webinar_records",
          "credibility",
          "credibility_verdict",
          "results_chart",
          "is_started",
          "is_finished",
          "progress",
          "quiz_components",
          "results",
          "scores",
          "student_assessments",
        ];

        return {
          url: `/api/assignment-report/${token}/assignment.json`,
          method: "GET",
          params: attemptId ? { assignment_attempt_id: attemptId } : undefined,
          headers: {
            "X-Requested-Fields": fields.join(","),
          },
        };
      },
      providesTags: (result, error, { token, attemptId }) => [
        { type: "ReportData", id: `${token}-${attemptId || "default"}` },
      ],
    }),

    getAssignmentReportResults: builder.query<
      GetAssignmentReportResultsResponse,
      GetAssignmentReportResultsRequest
    >({
      query: ({ token, attemptId, limit = 999 }) => ({
        url: `/api/assignment-report/${token}/results.json`,
        method: "GET",
        params: {
          assignment_attempt_id: attemptId,
          limit,
        },
      }),
      providesTags: (_result, _error, { token, attemptId }) => [
        { type: "ReportData", id: `results-${token}-${attemptId}` },
      ],
    }),

    getPublicAiReports: builder.query<
      PublicAiReportsResponse,
      GetPublicAiReportsRequest
    >({
      async queryFn({ attemptId, reportTypes, baseReportTypes, forceRegenerate = false }) {
        try {
          const baseUrl = `${process.env.NEXT_PUBLIC_API_RATING_URL}reports/ai/`;
          const url = new URL(`public/${attemptId}`, baseUrl);
          
          if (reportTypes && reportTypes.length > 0) {
            reportTypes.forEach(type => {
              url.searchParams.append('report_types', type);
            });
          }

          if (baseReportTypes && baseReportTypes.length > 0) {
            baseReportTypes.forEach(type => {
              url.searchParams.append('base_report_types', type);
            });
          }

          if (forceRegenerate) {
            url.searchParams.append('force_regenerate', 'true');
          }

          const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              // Эндпоинт может отдавать как JSON, так и SSE (text/event-stream).
              Accept: 'application/json, text/event-stream',
            },
          });

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: await response.text(),
              },
            };
          }

          // Ответ бывает двух форм:
          //  • JSON: { attempt_id, reports: { ... }, ... }
          //  • SSE (text/event-stream): последовательность `event: result` /
          //    `data: { "report_type": "...", "data": {...} }` — собираем их в reports.
          const raw = await response.text();
          const trimmed = raw.trimStart();

          let data: PublicAiReportsResponse;
          if (trimmed.startsWith('{')) {
            data = JSON.parse(raw);
          } else {
            const reports: Record<string, unknown> = {};
            for (const block of raw.split('\n\n')) {
              const lines = block.trim().split('\n');
              const eventLine = lines.find((l) => l.startsWith('event:'));
              const dataLine = lines.find((l) => l.startsWith('data:'));
              if (!dataLine) continue;
              // если event указан — берём только result; иначе полагаемся на report_type в payload
              if (eventLine && eventLine.replace('event:', '').trim() !== 'result') continue;
              try {
                const payload = JSON.parse(dataLine.replace('data:', '').trim());
                if (payload && typeof payload.report_type === 'string') {
                  reports[payload.report_type] = payload.data;
                }
              } catch {
                /* битый JSON — пропускаем */
              }
            }
            data = {
              attempt_id: attemptId,
              reports: reports as PublicAiReportsResponse['reports'],
              errors: {},
              success_count: Object.keys(reports).length,
              total_requested: (reportTypes?.length ?? 0),
              generated_at: '',
            };
          }
          return { data };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error',
            },
          };
        }
      },
      providesTags: (result, error, { attemptId }) =>
        result
          ? [{ type: "ReportData", id: `ai-public-${attemptId}` }]
          : ["ReportData"],
    }),
  }),
});

export const {
  useGetAssignmentReportInfoQuery,
  useGetAssignmentReportDataQuery,
  useGetAssignmentReportResultsQuery,
  useGetPublicAiReportsQuery,
} = publicReportApi;
